import React, { useState } from 'react';
import { Col, Row, formatAmountText } from '../../lib/utils';
import { useAmountInputCaret } from '../../lib/amountInputCaret';
import { colors } from '../../lib/colors';
import { Body2Regular, CaptionLight } from '../../lib/font';
import ModalAlert from '../modal/ModalAlert';
import { useRefundSubscription } from '@/features/facilitySubscription/hooks';
import { IFacilitySubscription } from '@/features/facilitySubscription/types';
import { getFacilityTransferLocks } from '@/features/facilitySubscription/transfer';
import { TransferredRefundNotice, useTransferredRefundInfo } from './SubscriptionTransferHistory';

const getMutationErrorMessage = (error: any, fallback: string) => error?.serverMessage || error?.message || fallback;

interface Props {
    isVisible: boolean;
    onClose: () => void;
    centerId: string;
    memberId: string;
    subscriptionId: string;
    subscriptionPrice?: number;
    createAlert: any;
    /** 양도 표식 판정용(없으면 기존 동작 그대로) */
    subscription?: IFacilitySubscription | null;
}

const SubscriptionRefundModal = ({ isVisible, onClose, centerId, memberId, subscriptionId, subscriptionPrice, createAlert, subscription }: Props) => {
    // 일부 양도한 원본: 금액을 빈칸으로 시작하고 확인 칸을 실어 보낸다(서버는 둘 다 없으면 409).
    const needsTransferAck = getFacilityTransferLocks(subscription).refundNeedsAck;
    // 창을 열 때마다 이력을 새로 읽는다. 다 읽기 전에는 환불을 막는다(낡은 안내로 금액을 정하지 않게).
    const transferredInfo = useTransferredRefundInfo({
        centerId,
        subscriptionId,
        enabled: isVisible && needsTransferAck && !!subscriptionId,
    });
    const refundBlockedByTransferInfo = needsTransferAck && !transferredInfo.ready;
    const refundMutation = useRefundSubscription();
    const [refundReason, setRefundReason] = useState('');
    // 콤마 없는 숫자 문자열만 담는다. 콤마는 아래 formatAmountText 로 표시할 때만 붙인다.
    const [refundAmount, setRefundAmount] = useState<string>('');
    const amountCaret = useAmountInputCaret({ allowDecimal: true });

    React.useEffect(() => {
        if (isVisible) {
            setRefundReason('');
            // 부모가 값을 갈아끼우는 자리 — 낡은 커서 복원을 버린다.
            amountCaret.dropPendingCaret();
            setRefundAmount(needsTransferAck ? '' : String(subscriptionPrice ?? 0));
        }
    }, [isVisible, subscriptionPrice, amountCaret, needsTransferAck]);

    const handleSubmit = async () => {
        if (refundBlockedByTransferInfo) return;
        const amount = Number(refundAmount);
        if (!amount || amount <= 0) {
            createAlert('환불 실패', '환불 금액을 입력해주세요.', true);
            return;
        }
        if (subscriptionPrice != null && amount > subscriptionPrice) {
            createAlert('환불 실패', '환불 금액이 결제 금액을 초과할 수 없습니다.', true);
            return;
        }
        try {
            await refundMutation.mutateAsync({
                centerId,
                subscriptionId,
                memberId,
                body: {
                    refundAmount: amount,
                    refundReason: refundReason.trim() || undefined,
                    ...(needsTransferAck ? { acknowledgeTransferred: true as const } : {}),
                },
            });
            createAlert('환불 완료', `${amount.toLocaleString()}원 환불이 처리되었습니다.`, false);
            onClose();
        } catch (error) {
            createAlert('환불 실패', getMutationErrorMessage(error, '환불 처리에 실패했습니다.'), true);
        }
    };

    if (!isVisible) return null;
    const inputClass = 'border border-[#E1E4E6] rounded-[8px] px-[12px] py-[10px] text-[14px] w-full';

    return (
        <ModalAlert
            modalVisible={isVisible}
            setModalVisible={onClose}
            title="이용권 환불"
            content=""
            cancelButtonText="취소"
            confirmButtonText="환불"
            callBack={handleSubmit}
            cancelCallBack={onClose}
            customComponent={
                <Col className="gap-[16px] w-[400px]">
                    <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>이용권 환불</span>

                    {/* 결제 금액 안내 */}
                    {subscriptionPrice != null && (
                        <div
                            style={{
                                padding: '12px 16px',
                                borderRadius: 8,
                                backgroundColor: '#F8FAFC',
                                border: '1px solid #E5E7EB',
                            }}
                        >
                            <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 13, color: '#6B7280' }}>결제 금액</span>
                                <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{Number(subscriptionPrice).toLocaleString()}원</span>
                            </Row>
                        </div>
                    )}

                    {needsTransferAck && <TransferredRefundNotice info={transferredInfo} />}

                    {/* 환불 금액 입력 (전액/부분 모두 가능) */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>환불 금액</Body2Regular>
                        <input
                            className={inputClass}
                            type="text"
                            inputMode="numeric"
                            value={formatAmountText(refundAmount)}
                            onChange={(e) => {
                                // 원칙 1 — 패턴에 안 맞으면 값을 아예 바꾸지 않는다.
                                const digits = amountCaret.changeAmount(e, refundAmount);
                                if (digits == null) return;
                                setRefundAmount(digits);
                            }}
                            // 붙여넣기는 한 번에 값이 확정되므로 정규형만 받는다(CSV 조각 차단).
                            onPaste={amountCaret.pasteAmount}
                            onDrop={amountCaret.dropAmount}
                            placeholder={needsTransferAck ? '돌려줄 금액을 적어 주세요' : '환불할 금액'}
                            autoFocus
                        />
                        {subscriptionPrice != null && !needsTransferAck && (
                            <Row style={{ gap: 6, marginTop: 4 }}>
                                <button
                                    type="button"
                                    onClick={() => { amountCaret.dropPendingCaret(); setRefundAmount(String(subscriptionPrice)); }}
                                    style={{
                                        padding: '4px 10px',
                                        fontSize: 11,
                                        borderRadius: 6,
                                        border: '1px solid #E5E7EB',
                                        backgroundColor: '#F9FAFB',
                                        color: '#374151',
                                        cursor: 'pointer',
                                    }}
                                >
                                    전액
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { amountCaret.dropPendingCaret(); setRefundAmount(String(Math.floor(subscriptionPrice / 2))); }}
                                    style={{
                                        padding: '4px 10px',
                                        fontSize: 11,
                                        borderRadius: 6,
                                        border: '1px solid #E5E7EB',
                                        backgroundColor: '#F9FAFB',
                                        color: '#374151',
                                        cursor: 'pointer',
                                    }}
                                >
                                    반액
                                </button>
                            </Row>
                        )}
                    </Col>

                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>환불 사유 (선택)</Body2Regular>
                        <textarea
                            className={`${inputClass} resize-none`}
                            rows={2}
                            placeholder="사유를 입력해주세요"
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                        />
                    </Col>
                    <Col className="p-[10px] bg-[#FEF2F2] rounded-[8px]">
                        <CaptionLight color="#DC2626">⚠️ 환불 처리 후 복구가 불가능합니다</CaptionLight>
                    </Col>
                    <Row style={{ gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '8px 24px',
                                borderRadius: 8,
                                fontSize: 14,
                                fontWeight: 600,
                                color: '#374151',
                                backgroundColor: '#F3F4F6',
                                border: '1px solid #E5E7EB',
                                cursor: 'pointer',
                            }}
                        >
                            취소
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={refundMutation.isLoading || refundBlockedByTransferInfo}
                            style={{
                                padding: '8px 24px',
                                borderRadius: 8,
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'white',
                                backgroundColor: '#EF4444',
                                border: 'none',
                                cursor: 'pointer',
                                opacity: refundMutation.isLoading || refundBlockedByTransferInfo ? 0.6 : 1,
                            }}
                        >
                            {refundMutation.isLoading ? '처리중...' : '환불 처리'}
                        </button>
                    </Row>
                </Col>
            }
        />
    );
};

export default SubscriptionRefundModal;
